/**
 * Almacen Entity - Warehouse management
 * Manages inventory, door state, and interactions with the warehouse
 */

/**
 * Parse item to extract base name and position
 */
function parseItem(item) {
    const match = item.match(/^(.+?)\(([^)]+)\)$/);
    if (match) {
        return {
            baseName: match[1].trim(),
            position: match[2],
            fullName: item
        };
    }
    return {
        baseName: item,
        position: null,
        fullName: item
    };
}

/**
 * Compare positions for sorting
 */
function comparePositions(posA, posB) {
    if (!posA && !posB) return 0;
    if (!posA) return 1;
    if (!posB) return -1;

    const parsePos = (pos) => {
        const match = pos.match(/(\d+),\s*(\d+),\s*(\d+)/);
        if (!match) return [0, 0, 0];
        return [parseInt(match[1]), parseInt(match[2]), parseInt(match[3])];
    };

    const [x1, y1, z1] = parsePos(posA);
    const [x2, y2, z2] = parsePos(posB);

    if (x1 !== x2) return x1 - x2;
    if (y1 !== y2) return y1 - y2;
    return z1 - z2;
}

/**
 * Sort items by name first, then by position
 */
function sortItems(items) {
    return items.sort((a, b) => {
        const itemA = parseItem(a);
        const itemB = parseItem(b);

        const nameComparison = itemA.baseName.localeCompare(itemB.baseName);
        if (nameComparison !== 0) {
            return nameComparison;
        }

        return comparePositions(itemA.position, itemB.position);
    });
}

class Almacen {
    constructor() {
        this.opened = false;
        this.contains = [];
    }

    /**
     * Open the warehouse shelves
     * @returns {boolean} true if operation successful, false if already open
     */
    open() {
        if (this.opened) {
            return false; // Already open
        }
        this.opened = true;
        return true;
    }

    /**
     * Close the warehouse shelves
     * @returns {boolean} true if operation successful, false if already closed
     */
    close() {
        if (!this.opened) {
            return false; // Already closed
        }
        this.opened = false;
        return true;
    }

    /**
     * Check if warehouse is open
     * @returns {boolean}
     */
    isOpen() {
        return this.opened;
    }

    /**
     * Add item to warehouse inventory
     * @param {string} item - Item to add
     */
    addItem(item) {
        this.contains.push(item);
    }

    /**
     * Remove item from warehouse inventory
     * @param {string} item - Item to remove (optional, removes last if not specified)
     * @returns {string|null} - Removed item or null if warehouse empty
     */
    removeItem(item = null) {
        if (this.contains.length === 0) {
            return null;
        }

        if (item) {
            const index = this.contains.indexOf(item);
            if (index !== -1) {
                const removed = this.contains.splice(index, 1)[0];
                console.log(`[Almacen.removeItem] Removed "${removed}" from position ${index}`);
                return removed;
            }
            console.log(`[Almacen.removeItem] Item "${item}" not found in Almacen`);
            return null;
        }

        const removed = this.contains.pop();
        console.log(`[Almacen.removeItem] Removed last item: "${removed}"`);
        return removed;
    }

    /**
     * Get current state
     * @returns {Object}
     */
    getState() {
        // Sort items by name first, then by position (ascending)
        const sortedContains = sortItems ? sortItems([...this.contains]) : [...this.contains];

        return {
            opened: this.opened ? "abierto" : "cerrado",
            contains: sortedContains
        };
    }

    /**
     * Reset to initial state
     */
    reset() {
        this.opened = false;
        this.contains = [];
    }
}

// Universal export (Node.js + React)
if (typeof module !== 'undefined' && module.exports) {
    module.exports = Almacen;
}
export default Almacen;
